import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import {
  type Building,
  buildingIndex,
  buildingLevel,
  buildingMaxHp,
  type EntityId,
  isCity,
  isHidden,
  isMilitary,
  mintOrder,
  type PlayerId,
  type SimState,
  type TileVisibility,
  territoryOwners,
} from '../sim';

/**
 * What the player knows about each explored tile: its state the last time it
 * was in sight. Tiles out of sight are drawn and described from this memory
 * rather than the live sim state, so fog of war doesn't leak rivals'
 * buildings or gathering.
 *
 * Pure: no React, so it's unit-testable.
 */

export interface TileView {
  /** Gatherable amount left, in thousandths (sim/fixed.ts); null = never seen touched (full). */
  remaining: number | null;
  building: Building | null;
}

export type TileMemory = Map<HexTileMapKey, TileView>;

/** Records the current state of every visible tile. */
export function rememberVisible(
  memory: TileMemory,
  state: SimState,
  visible: Iterable<HexTileMapKey>
): void {
  const buildings = buildingIndex(state);
  for (const key of visible) {
    const b = buildings.get(key);
    memory.set(key, {
      remaining: state.tileResourceOverride[key] ?? null,
      building: b ? copyBuilding(b) : null,
    });
  }
}

/**
 * `b` without its `upgrading`: an upgrade in progress is its owner's secret
 * (snapshots carry it, the view must not show it of a rival), so every rival
 * building handed to the UI goes through this.
 */
function withoutUpgrade(b: Building): Building {
  if (!b.upgrading) return b;
  const { upgrading: _hidden, ...rest } = b;
  return rest;
}

function copyBuilding(b: Building): Building {
  return {
    ...withoutUpgrade(b),
    pos: { ...b.pos },
    ...(b.footprint ? { footprint: b.footprint.map((c) => ({ ...c })) } : {}),
    ...(b.levelTicks ? { levelTicks: [...b.levelTicks] } : {}),
  };
}

/**
 * A remembered rival city that has since fallen: its live border and marker
 * are already gone (`knownTerritories`, `fogCityMarkers`), so memory drops it
 * too. Other remembered buildings stay as last seen.
 */
function fallenCity(state: SimState, rules: RuleSet, b: Building): boolean {
  return isCity(b, rules) && !state.buildings[b.id];
}

/**
 * Returns a lookup of what `playerId` should see on a tile: live state while
 * it's visible; otherwise the remembered resources and rival building (a
 * fallen city left out: `fallenCity`), plus their own building (which they
 * always know about).
 */
export function tileViewer(
  memory: TileMemory,
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): (key: HexTileMapKey) => TileView {
  const buildings = buildingIndex(state);
  return (key) => {
    const live = buildings.get(key) ?? null;
    if (getVisibility(key) === 'visible') {
      return { remaining: state.tileResourceOverride[key] ?? null, building: live };
    }
    const seen = memory.get(key);
    const own = live && live.owner === playerId ? live : null;
    const seenRival =
      seen?.building && seen.building.owner !== playerId && !fallenCity(state, rules, seen.building)
        ? seen.building
        : null;
    return { remaining: seen?.remaining ?? null, building: own ?? seenRival };
  };
}

/**
 * Every building `playerId` knows of: their own (live), rivals' on tiles in
 * sight (live), and rivals' as last seen elsewhere. A rival city destroyed
 * out of sight stays known until its tile is seen again; one founded out of
 * sight isn't known until seen. Used for rival territory borders.
 */
export function knownBuildings(
  memory: TileMemory,
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): Building[] {
  const result: Building[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.owner === playerId) result.push(b);
    else if (getVisibility(getHexTileMapKey(b.pos)) === 'visible') result.push(withoutUpgrade(b));
  }
  for (const [key, view] of memory) {
    const b = view.building;
    if (
      !b ||
      b.owner === playerId ||
      getVisibility(key) === 'visible' ||
      fallenCity(state, rules, b)
    ) {
      continue;
    }
    // A grown building is remembered on each of its tiles: list it once.
    if (getHexTileMapKey(b.pos) !== key) continue;
    result.push(b);
  }
  return result;
}

/**
 * The state with only the buildings `playerId` knows of (their own, and the
 * rivals' in `known`, each live): out of sight, rival territory is
 * partitioned from these alone, so a city the player has never seen can't
 * carve a hole in a seen city's border in the fog.
 */
function knownState(state: SimState, known: readonly Building[], playerId: PlayerId): SimState {
  const seen = new Set(known.map((b) => b.id));
  return {
    ...state,
    buildings: Object.fromEntries(
      Object.values(state.buildings)
        .filter((b) => b.owner === playerId || seen.has(b.id))
        .map((b) => [b.id, b])
    ),
  };
}

/**
 * Each player's territory as `playerId` should draw it (player → tile keys),
 * a tile in at most one player's set, so borders never overlap:
 * - their own territory (live `territoryOwners`): where they can build, and
 *   exactly where their units are sheltered;
 * - a tile in sight: whoever really holds it, which is what shelters a rival
 *   unit standing there, even when the rival city behind it was never seen;
 * - out of sight: a partition of only the cities the player has seen (on
 *   their tile, now or before: `known`, from `knownBuildings`), each at its
 *   live level, so a seen city's growth, shrinking and fall show at once (an
 *   accepted reveal: a city once seen is tracked).
 * Fog: a rival city never seen only shows on land in sight, and never cuts
 * into a seen city's land out of sight.
 */
export function knownTerritories(
  state: SimState,
  known: readonly Building[],
  playerId: PlayerId,
  rules: RuleSet,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): Map<PlayerId, Set<HexTileMapKey>> {
  const own = new Set<HexTileMapKey>();
  const result = new Map<PlayerId, Set<HexTileMapKey>>([[playerId, own]]);
  const add = (owner: PlayerId, key: HexTileMapKey) => {
    let tiles = result.get(owner);
    if (!tiles) {
      tiles = new Set();
      result.set(owner, tiles);
    }
    tiles.add(key);
  };
  const live = territoryOwners(state, rules);
  for (const [key, cityId] of live) {
    const owner = state.buildings[cityId]?.owner;
    if (owner === playerId) own.add(key);
    else if (owner !== undefined && getVisibility(key) === 'visible') add(owner, key);
  }
  const fog = knownState(state, known, playerId);
  for (const [key, cityId] of territoryOwners(fog, rules)) {
    if (own.has(key) || getVisibility(key) === 'visible') continue;
    const owner = fog.buildings[cityId]?.owner;
    if (owner !== undefined && owner !== playerId) add(owner, key);
  }
  return result;
}

/**
 * Which edges of `drawn` (from `knownTerritories`) to outline: own edges
 * always; a rival's only on explored tiles, and never an edge where the
 * viewer's sight ends (a tile in sight beside one out of sight) unless it's a
 * boundary the viewer knows of: the tile beyond is drawn as someone else's,
 * or the tile in sight belongs to a rival city they've seen (whose land out
 * of sight is drawn, so the tile beyond isn't that city's). A never-seen
 * city is drawn only on land in sight, so without this its outline would
 * close along the edge of sight, through the middle of their land.
 */
export function borderEdgeShown(
  state: SimState,
  known: readonly Building[],
  drawn: ReadonlyMap<PlayerId, ReadonlySet<HexTileMapKey>>,
  playerId: PlayerId,
  rules: RuleSet,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): (owner: PlayerId, key: HexTileMapKey, nbKey: HexTileMapKey) => boolean {
  const seen = new Set(known.map((b) => b.id));
  let owners: ReadonlyMap<HexTileMapKey, string> | undefined;
  return (owner, key, nbKey) => {
    if (owner === playerId) return true;
    const vis = getVisibility(key);
    if (vis === 'unexplored') return false;
    if (vis !== 'visible' || getVisibility(nbKey) === 'visible') return true;
    for (const [other, tiles] of drawn) {
      if (other !== owner && tiles.has(nbKey)) return true;
    }
    owners ??= territoryOwners(state, rules);
    const cityId = owners.get(key);
    return cityId !== undefined && seen.has(cityId);
  };
}

/**
 * Rival cities `playerId` has seen (`known`, from `knownBuildings`) that
 * still stand and whose tile is out of sight: where to draw a marker so they
 * can be aimed at through the fog. Whether one stands is live, like their
 * borders (`knownTerritories`): one destroyed out of sight loses its marker
 * at once; a city never seen gets none. Each is the city as last seen (its
 * `known` copy), so its level and hp don't leak through the fog.
 */
export function fogCityMarkers(
  state: SimState,
  rules: RuleSet,
  known: readonly Building[],
  playerId: PlayerId,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): Building[] {
  const result: Building[] = [];
  for (const seen of known) {
    const b = state.buildings[seen.id];
    if (!b || b.owner === playerId || !isCity(b, rules)) continue;
    if (getVisibility(getHexTileMapKey(b.pos)) !== 'visible') result.push(seen);
  }
  return result;
}

/**
 * The rival city a click on `key` out of sight is aimed at: a city the
 * player knows of (`known`) whose tile is out of sight within 1 of `key`
 * (its tile, or the ring a city model spreads over), nearest first, then
 * the oldest; null otherwise, or on a tile in sight (aim at what's there).
 * As last seen, so a right-click on a fogged city's edge sends an attack at
 * it rather than a move onto its doorstep, without revealing anything.
 */
export function fogCityAt(
  known: readonly Building[],
  rules: RuleSet,
  playerId: PlayerId,
  key: HexTileMapKey,
  getVisibility: (key: HexTileMapKey) => TileVisibility
): Building | null {
  if (getVisibility(key) === 'visible') return null;
  const [q, r] = key.split(',').map(Number);
  let best: Building | null = null;
  let bestDist = 2;
  for (const b of known) {
    if (b.owner === playerId || !isCity(b, rules)) continue;
    if (getVisibility(getHexTileMapKey(b.pos)) === 'visible') continue;
    const dist = axialHexDistance(b.pos, { q, r });
    if (dist < bestDist || (dist === bestDist && best && mintOrder(b.id) < mintOrder(best.id))) {
      best = b;
      bestDist = dist;
    }
  }
  return best;
}

/** What `cityIntel` tells about a rival city. */
export interface CityIntel {
  /** The city as the player knows it: live while its tile is in sight, else as last seen. */
  city: Building;
  /** Whether `city` is live (its tile in sight) or as last seen. */
  seen: 'live' | 'remembered';
  level: number;
  /** Hp and max hp, in thousandths (sim/fixed.ts), as `seen`; max hp without bonuses the player can't see (barracks, last stand). */
  hp: number;
  maxHp: number;
  /** The rival's military units the player sees right now in that city's territory (outside buildings). */
  defenders: number;
}

/**
 * Defence intel on rival city `cityId` for `playerId` (fog-safe; null when
 * it isn't a rival city they know of, from `known` = `knownBuildings`):
 * its level and hp as last seen (live while its tile is in sight), and how
 * many of its owner's military units they see in its territory now. Only
 * tiles in sight are counted, each by its real holder, as `knownTerritories`
 * draws them, so nothing under the fog is counted. `owners`
 * (`territoryOwners`) may be passed in when asking about several cities.
 */
export function cityIntel(
  state: SimState,
  rules: RuleSet,
  known: readonly Building[],
  playerId: PlayerId,
  cityId: EntityId,
  getVisibility: (key: HexTileMapKey) => TileVisibility,
  owners: ReadonlyMap<HexTileMapKey, EntityId> = territoryOwners(state, rules)
): CityIntel | null {
  const city = known.find((b) => b.id === cityId);
  if (!city || city.owner === playerId || !isCity(city, rules)) return null;
  const seen = getVisibility(getHexTileMapKey(city.pos)) === 'visible' ? 'live' : 'remembered';
  let defenders = 0;
  for (const u of Object.values(state.units)) {
    if (u.owner !== city.owner || u.inside || isHidden(rules, u) || !isMilitary(rules, u.type)) {
      continue;
    }
    const key = getHexTileMapKey(u.pos);
    if (getVisibility(key) === 'visible' && owners.get(key) === cityId) defenders++;
  }
  // A rival's bonuses could come from buildings under fog: base max only.
  const base = buildingMaxHp(state, rules, { ...city, lastStand: undefined }, new Map());
  return {
    city: withoutUpgrade(city),
    seen,
    level: buildingLevel(city),
    hp: city.hp,
    maxHp: Math.max(base, city.hp),
    defenders,
  };
}
